import { SnapshotBase, SnapshotResponseBase } from "@/shared/types";
import { ObservableObject, batch, observable } from "@legendapp/state";
import dayjs from "dayjs";
import { StatusManager } from "../status-manager";
import { convertToUtc, getTracks, handleError } from "../utils";
import { getTimezone } from "@/shared";
import { toast } from "react-hot-toast";
import { BufferTimeRanges } from "../time-ranges";
import { ControlEvent, ControlEventArgs, emitter } from "../../event-emitter";
import { SnapshotsSourceBuffer } from "./snapshots-source-buffer";
import { TRACK_SNAPSHOT_MAXIMUM_GAP_THRESHOLD } from "@/shared/consts";

const getSavedConfig = () => {
  const savedConfig = localStorage.getItem("config");

  if (!savedConfig) {
    return null;
  }

  return JSON.parse(savedConfig) as Config;
};

const DEFAULT_CONFIG: Config = {
  chunkSize: 2000,
  startOffset: 0,
  pollingInterval: 500,
  retryInterval: 500,
  isLive: false,
  dateRangeEnd: null,
  timezone: getTimezone(),
};

export class TracksBufferLoader<
  TSnapshot extends SnapshotBase,
> extends StatusManager {
  // The actual time range where data exist
  public actualRange$: ObservableObject<TimeRange>;
  public timeRanges: BufferTimeRanges<TSnapshot>;
  private nextMinDateTime: Date | null;
  public visibleTracks$: ObservableObject<{
    tracks: Record<string, true>;
  }> = observable<{
    tracks: Record<string, true>;
  }>({ tracks: {} });

  private sourceBuffer: SnapshotsSourceBuffer<TSnapshot>;
  private timeoutId: NodeJS.Timeout | null = null;
  private retryId: NodeJS.Timeout | null = null;
  private queryFn: Options<TSnapshot>["queryFn"];
  private signal: AbortController | null = null;
  private isImported = false;
  private isInitialized = false;

  public currentTime = 0;
  public config: Config = getSavedConfig() || DEFAULT_CONFIG;
  private retryParams: RetryParams | null = null;

  static BUFFER_WINDOW_SIZE = 1000 * 30; // 30 seconds

  constructor(options: Options<TSnapshot>) {
    super();

    this.queryFn = options.queryFn;
    this.sourceBuffer =
      options.sourceBuffer || new SnapshotsSourceBuffer<TSnapshot>();
    this.actualRange$ = observable<TimeRange>({
      start: null,
      end: null,
    });
    this.timeRanges = new BufferTimeRanges(this);
    this.nextMinDateTime = null;

    // this.onTimeChanged = this.onTimeChanged.bind(this);
    emitter.on(ControlEvent.TimeChanged, ({ currentTime }) => {
      this.currentTime = currentTime;
    });
  }

  /**
   *
   * @param start Start date time of the timeline
   * @param end End date time of the timeline
   * @param offset Offset is used in Live mode to fetch the next set of data
   * @returns Promise<TrackSnapshotResult>
   */

  async init(start: Date, end: Date | null, config?: Partial<Config>) {
    this.setStatus("loading");

    this.isInitialized = true;

    this.config = { ...this.config, ...config };

    try {
      this.setFetchStatus("fetching");
      const result = await this.fetchSegmentObjects(start, end);

      // If there are no results, we need to fetch the next set of data
      if (
        result.results.length === 0 &&
        result.min_datetime &&
        result.max_datetime
      ) {
        this.init(new Date(result.min_datetime), new Date(result.max_datetime));
      }

      // If server returns nothing in history mode, we need to display toast
      if (
        result.results.length === 0 &&
        !result.min_datetime &&
        !result.max_datetime &&
        !this.config.isLive &&
        (!result.next_min_datetime ||
          !this.isWithinRange(new Date(result.next_min_datetime)))
      ) {
        toast("There are no track snapshots in the selected date range", {
          style: {
            background: "#333",
            color: "white",
          },
          position: "bottom-center",
        });
        return;
      }

      // If there are no results within the time range, we need to fetch the data from the next start
      if (
        result.results.length === 0 &&
        !result.min_datetime &&
        !result.max_datetime &&
        result.next_min_datetime
      ) {
        // Register empty time range
        this.timeRanges.mergeTimeRanges(
          start,
          new Date(result.next_min_datetime),
          true
        );
        const nextMaxDateTime = this.getNextMaxDateTime(
          result.next_min_datetime
        );
        this.init(new Date(result.next_min_datetime), nextMaxDateTime);
      }

      // If server returns nothing in live mode, we need to retry fetching the data
      if (
        result.results.length === 0 &&
        !result.min_datetime &&
        !result.max_datetime &&
        !result.next_min_datetime &&
        this.config.isLive &&
        end
      ) {
        this.retryFetchSegmentObjects(start, end);
      }

      // If there are results, we need to check if the start date is aligned with the start date of the timeline
      // If not, we need to fetch the data from the start date of the timeline
      // If yes, we need to create empty time range and continue buffering
      if (
        result.min_datetime &&
        result.max_datetime &&
        result.results.length > 0
      ) {
        const isStartAligned =
          new Date(start).getTime() === new Date(result.min_datetime).getTime();

        if (!isStartAligned) {
          if (this.config.isLive) {
            this.init(
              new Date(result.min_datetime),
              new Date(result.max_datetime)
            );
            return;
          } else {
            // Register empty time range
            this.timeRanges.mergeTimeRanges(
              start,
              new Date(result.min_datetime),
              true
            );
          }
        }

        const minDateTime = new Date(result.min_datetime);
        const maxDateTime = new Date(result.max_datetime);

        this.config.onSuccess?.(
          maxDateTime,
          getTracks<TSnapshot>(result.results)
        );

        this.timeRanges.mergeTimeRanges(
          new Date(result.min_datetime),
          new Date(result.max_datetime)
        );

        // This will set actual timeline boundaries
        batch(() => {
          this.actualRange$.start.set(minDateTime);
          this.actualRange$.end.set(maxDateTime);
        });

        this.nextMinDateTime = new Date(result.next_min_datetime);
        this.sourceBuffer.setStartTime(minDateTime);
        this.sourceBuffer.appendBuffer(result.results);

        this.startBuffering();
        return Promise.resolve(result);
      }
    } catch (err) {
      handleError(err);
      this.setStatus("error");
    } finally {
      this.setFetchStatus("idle");
    }
  }

  async onTimeChanged({ currentTime }: ControlEventArgs["TIME_CHANGED"]) {
    this.currentTime = currentTime;
    console.log(currentTime);
  }

  async fetchSegmentObjects(
    start: Date,
    end: Date | null,
    page?: number,
    limit?: number
  ): Promise<SnapshotResponseBase<TSnapshot>> {
    const startUtc = convertToUtc(start);
    const endUtc = end ? convertToUtc(end) : null;

    const abortController = new AbortController();
    this.signal = abortController;

    return this.queryFn({
      fromDateTime: startUtc,
      toDateTime: endUtc,
      page: page || 1,
      limit: limit || this.config.chunkSize,
      signal: abortController,
      cameraIds: this.config.cameraIds,
    });
  }

  /**
   * Builder method that starts buffering live or historical data based on the offset
   *
   * @param offset Offset is used in Live mode to fetch the next set of data
   */
  async startBuffering() {
    if (this.config.isLive) {
      this.startBufferingLive();
    } else {
      this.startBufferingHistory();
    }
  }

  async startBufferingLive() {
    if (this.timeoutId) {
      return;
    }

    this.poll();
  }

  async startBufferingHistory(fromDateTime?: Date) {
    if (this.timeoutId) {
      return;
    }

    if (fromDateTime) {
      this.nextMinDateTime = fromDateTime;
    }

    this.poll();
  }

  private async poll() {
    this.timeoutId = setTimeout(
      () => this.doBuffer(),
      this.config.pollingInterval
    );
  }

  async doBuffer() {
    const nextMinDateTime = this.nextMinDateTime;

    if (nextMinDateTime) {
      if (!this.isWithinRange(nextMinDateTime)) {
        this.stopPolling();
      }

      try {
        const nextMaxDateTime = this.getNextMaxDateTime(nextMinDateTime);
        const result = await this.fetchSegmentObjects(
          nextMinDateTime,
          nextMaxDateTime
        );

        if (result.next_min_datetime) {
          if (result.results.length > 0) {
            const resultNextMinDateTime = new Date(result.next_min_datetime);
            const resultMinDateTime = new Date(result.min_datetime);
            const resultMaxDateTime = new Date(result.max_datetime);

            this.config.onSuccess?.(
              resultNextMinDateTime,
              getTracks(result.results)
            );

            if (
              resultNextMinDateTime.getTime() - resultMaxDateTime.getTime() >
                TRACK_SNAPSHOT_MAXIMUM_GAP_THRESHOLD &&
              this.isWithinRange(resultNextMinDateTime)
            ) {
              this.timeRanges.mergeTimeRanges(
                resultMaxDateTime,
                resultNextMinDateTime,
                true
              );
            }

            this.timeRanges.mergeTimeRanges(
              resultMinDateTime,
              resultMaxDateTime
            );

            this.skipToBufferEnd(resultMaxDateTime, resultNextMinDateTime);
            this.actualRange$.end.set(resultNextMinDateTime);
            this.sourceBuffer.appendBuffer(result.results);

            if (!this.isWithinRange(new Date(result.next_min_datetime))) {
              this.stopPolling();
            } else {
              this.poll();
            }
          } else {
            // Register empty time range
            this.timeRanges.mergeTimeRanges(
              new Date(nextMinDateTime),
              new Date(result.next_min_datetime),
              true
            );
            this.stopPolling();
            this.init(
              new Date(result.next_min_datetime),
              this.getNextMaxDateTime(result.next_min_datetime)
            );
          }
        } else {
          if (this.config.isLive) {
            this.retryFetchSegmentObjects(
              nextMinDateTime,
              nextMaxDateTime as Date
            );
          } else {
            this.stopPolling();
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
  }

  skipToBufferEnd(maxDateTime: Date, nextMinDateTime: Date) {
    // Skip buffering already buffered data when seeking back in timeline
    const bufferEnd = this.timeRanges.findBufferEnd(maxDateTime);
    if (bufferEnd) {
      this.nextMinDateTime = bufferEnd;
    } else {
      this.nextMinDateTime = nextMinDateTime;
    }
  }

  public stopPolling() {
    if (!this.timeoutId) {
      return;
    }

    this.signal?.abort("Stop buffer");
    clearInterval(this.timeoutId);
    this.timeoutId = null;
  }

  private isWithinRange(dateTime: Date) {
    if (!this.config.dateRangeEnd) return true;

    return dateTime.getTime() <= this.config.dateRangeEnd.getTime();
  }

  private async retryFetchSegmentObjects(minDateTime: Date, maxDateTime: Date) {
    if (this.timeoutId) {
      clearInterval(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.retryId) {
      clearInterval(this.retryId);
      this.retryId = null;
    }

    this.retryId = setInterval(async () => {
      if (minDateTime && maxDateTime) {
        this.retryParams = {
          minDateTime,
          maxDateTime,
        };
        const result = await this.fetchSegmentObjects(minDateTime, maxDateTime);

        if (result.next_min_datetime) {
          if (this.retryId) {
            clearInterval(this.retryId);
            this.retryId = null;
          }

          if (!result.min_datetime && !result.max_datetime) {
            // Register empty time range
            this.timeRanges.mergeTimeRanges(
              minDateTime,
              new Date(new Date(result.next_min_datetime).getTime() + 100 / 3),
              true
            );
          } else {
            const start = new Date(result.min_datetime);
            const end = new Date(result.max_datetime);

            // This will set actual timeline boundaries
            batch(() => {
              if (!this.actualRange$.start.peek()) {
                this.actualRange$.start.set(start);
              }
              this.actualRange$.end.set(end);
            });

            this.timeRanges.mergeTimeRanges(
              new Date(result.min_datetime),
              new Date(result.max_datetime)
            );
            this.config.onSuccess?.(end, getTracks(result.results));
            this.sourceBuffer.setStartTime(start, true);
            this.sourceBuffer.appendBuffer(result.results);
          }

          this.nextMinDateTime = new Date(result.next_min_datetime);
          this.startBufferingLive();
        }
      } else {
        this.stopRetry();
        alert("Missing minDateTime and maxDateTime");
      }
    }, this.config.retryInterval);
  }

  public stopRetry() {
    if (!this.retryId) {
      return;
    }

    clearInterval(this.retryId);
    this.retryId = null;
    this.retryParams = null;
  }

  getTracks() {
    return this.sourceBuffer.getKeys();
  }

  getVisibleTracks() {
    return Object.keys(this.visibleTracks$.tracks.peek());
  }

  public getBuffer() {
    return this.sourceBuffer.getInternalBuffer();
  }

  public getBufferCache() {
    return this.sourceBuffer.getKeys();
  }

  private getNextMaxDateTime(startDate: Date | string) {
    const nextMaxDateTime = dayjs(startDate)
      .add(this.config.chunkSize, "millisecond")
      .toDate();

    if (!this.isWithinRange(nextMaxDateTime)) {
      return this.config.dateRangeEnd;
    }

    return nextMaxDateTime;
  }

  public get tracksBufferSizeMap() {
    return this.sourceBuffer.bufferSizeMap;
  }

  public getNextSnapshot(datetime: number, direction: "forward" | "backward") {
    return this.sourceBuffer.getNextSnapshotDatetime(datetime, direction);
  }

  public get isImportMode() {
    return this.isImported;
  }

  public flushBuffer(durationInSec: number, onFlush?: () => void) {
    return this.sourceBuffer.flushBuffer(
      durationInSec,
      this.currentTime,
      onFlush
    );
  }

  public updateConfig(config: Partial<typeof this.config>) {
    this.config = {
      ...this.config,
      ...config,
    };

    if (this.config.pollingInterval !== undefined && this.timeoutId) {
      clearInterval(this.timeoutId);
      this.timeoutId = null;
      this.startBuffering();
    }

    if (
      this.config.retryInterval !== undefined &&
      this.retryId &&
      this.retryParams
    ) {
      clearInterval(this.retryId);
      this.retryId = null;
      this.retryFetchSegmentObjects(
        this.retryParams.minDateTime,
        this.retryParams.maxDateTime
      );
    }
  }

  public import(
    json: SnapshotResponseBase<TSnapshot>["results"],
    startDate: Date,
    endDate: Date
  ) {
    this.isImported = true;
    this.sourceBuffer.clear();

    batch(() => {
      this.actualRange$.start.set(startDate);
      this.actualRange$.end.set(endDate);
      this.timeRanges.mergeTimeRanges(startDate, endDate);
    });
    this.sourceBuffer.setStartTime(startDate);
    this.sourceBuffer.appendBuffer(json);
  }

  /**
   * Returns snapshot to draw
   *
   * @param key track_id
   * @param datetime current timestamp of the timeline
   * @returns TrackSnapshot
   */
  public getSnapshotToDraw(key: string, datetime: number) {
    const snapshot = this.sourceBuffer.findSnapshot(key, datetime);

    if (snapshot) {
      this.visibleTracks$.tracks.assign({ [key]: true });
    } else {
      if (key) {
        this.visibleTracks$.tracks[key].delete();
      }
    }

    return snapshot;
  }

  public seekTo(dateTime: Date) {
    if (!this.isInitialized || this.isImported) return;

    this.stopPolling();

    if (!this.isWithinRange(dateTime)) return;

    const bufferEnd = this.timeRanges.findBufferEnd(dateTime);

    console.log(bufferEnd);

    if (bufferEnd) {
      this.startBufferingHistory(bufferEnd);
    } else {
      this.startBufferingHistory(dateTime);
    }
  }

  public isBufferEmpty$() {
    return this.sourceBuffer.isEmpty;
  }

  public destroy() {
    this.stopPolling();
    this.stopRetry();

    batch(() => {
      this.actualRange$.start.set(null);
      this.actualRange$.end.set(null);
      this.nextMinDateTime = null;
    });
    this.timeRanges.clear();
    this.visibleTracks$.tracks.set({});
    this.sourceBuffer.clear();
    this.isImported = false;
    this.isInitialized = false;

    emitter.off(ControlEvent.TimeChanged, this.onTimeChanged);
  }
}

type TimeRange = {
  start: Date | null;
  end: Date | null;
};

type Options<TSnapshot extends SnapshotBase> = {
  sourceBuffer?: SnapshotsSourceBuffer<TSnapshot>;
  queryFn: (params: {
    page?: number;
    limit?: number;
    fromDateTime?: string;
    toDateTime?: string | null;
    cameraIds?: string;
    signal?: AbortController;
  }) => Promise<SnapshotResponseBase<TSnapshot>>;
};

type Config = {
  startOffset: number;
  chunkSize: number;
  pollingInterval: number;
  retryInterval: number;
  isLive: boolean;
  timezone: string;
  cameraIds?: string;
  dateRangeEnd: Date | null;
  onSuccess?: (maxDateTime: Date, trackIds: string[]) => void;
  onFatalError?: () => void;
};

type RetryParams = {
  minDateTime: Date;
  maxDateTime: Date;
};
